
import { useState } from "react";
import { Sliders } from "lucide-react";
import { useDeck } from "./SectionDeck";
import styles from "./deck.module.css";

/**
 * Floating design tuner panel. Only renders when ?tune=1 is in the URL
 * (the deck reads the query param on mount and exposes `showTuner`).
 */
export default function DesignTuner() {
  const { tuner, setTuner, showTuner } = useDeck();
  const [open, setOpen] = useState(false);

  if (!showTuner) return null;

  return (
    <div className={styles.tuner}>
      <button
        className={styles.tunerToggle}
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle design tuner"
      >
        <Sliders size={12} />
        {open ? "Close" : "Tune"}
      </button>
      {open && (
        <div className={styles.tunerPanel}>
          <div className={styles.tunerTitle}>Design Tuner</div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Section transition</span>
              <span>{tuner.scrollDuration}ms</span>
            </div>
            <input
              className={styles.tunerSlider}
              type="range"
              min={300}
              max={1400}
              step={20}
              value={tuner.scrollDuration}
              onChange={(e) => setTuner("scrollDuration", Number(e.target.value))}
            />
          </div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Reveal direction</span>
              <span>{tuner.varyDir}</span>
            </div>
            <div className={styles.tunerSegment}>
              {(["varied", "uniform"] as const).map((m) => (
                <button
                  key={m}
                  className={`${styles.tunerSegBtn} ${tuner.varyDir === m ? styles.tunerSegBtnActive : ""}`}
                  onClick={() => setTuner("varyDir", m)}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Reveal duration</span>
              <span>{tuner.revealDuration}ms</span>
            </div>
            <input
              className={styles.tunerSlider}
              type="range"
              min={200}
              max={1500}
              step={20}
              value={tuner.revealDuration}
              onChange={(e) => setTuner("revealDuration", Number(e.target.value))}
            />
          </div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Travel distance</span>
              <span>{tuner.revealDistance}px</span>
            </div>
            <input
              className={styles.tunerSlider}
              type="range"
              min={10}
              max={140}
              step={2}
              value={tuner.revealDistance}
              onChange={(e) => setTuner("revealDistance", Number(e.target.value))}
            />
          </div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Text size</span>
              <span>{tuner.textScale}%</span>
            </div>
            <input
              className={styles.tunerSlider}
              type="range"
              min={85}
              max={120}
              step={1}
              value={tuner.textScale}
              onChange={(e) => setTuner("textScale", Number(e.target.value))}
            />
          </div>

          <div className={styles.tunerRow}>
            <div className={styles.tunerLabel}>
              <span>Background glow</span>
              <span>{tuner.bgGlow}%</span>
            </div>
            <input
              className={styles.tunerSlider}
              type="range"
              min={0}
              max={20}
              step={1}
              value={tuner.bgGlow}
              onChange={(e) => setTuner("bgGlow", Number(e.target.value))}
            />
          </div>

          <p className={styles.tunerHint}>
            Each scroll gesture eases through one section. Append <strong>?tune=1</strong>{" "}
            to any deck route to reveal this panel; remove the param to hide it.
            Arrow keys, Page Up/Down, and Space also navigate.
          </p>
        </div>
      )}
    </div>
  );
}
